// frontend/src/pages/Reports.js
import React, { useState, useEffect } from 'react';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    DocumentTextIcon,
    ChartBarIcon,
    ArrowDownTrayIcon,
    PlusIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';

const WIDGET_META = [
    { key: 'total_interns', title: 'Total Interns', unit: 'Interns' },
    { key: 'active_interns', title: 'Active Interns', unit: 'In progress' },
    { key: 'approved_hours', title: 'Approved Hours', unit: 'Hours', alt: 'hours_progress' },
    { key: 'completion_rate', title: 'Program Completion', unit: 'Completion Rate', suffix: '%' },
    { key: 'businesses_engaged', title: 'Businesses Engaged', unit: 'SME Partners', alt: 'businesses' },
    { key: 'process_maps', title: 'Process Maps', unit: 'Maps' },
    { key: 'pilots_active', title: 'Active Pilots', unit: 'Implementations' },
    { key: 'projects', title: 'Projects', unit: 'Projects' },
    { key: 'pending_reviews', title: 'Pending Reviews', unit: 'Items' },
    { key: 'support_escalations', title: 'Support Escalations', unit: 'Open / in progress' }
];

const Reports = () => {
    const [loading, setLoading] = useState(true);
    const [definitions, setDefinitions] = useState([]);
    const [myReports, setMyReports] = useState([]);
    const [overview, setOverview] = useState(null);
    const [generating, setGenerating] = useState(null);
    const [downloading, setDownloading] = useState(null);
    const [activeTab, setActiveTab] = useState('my-reports');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [defsRes, reportsRes, overviewRes] = await Promise.allSettled([
                axios.get('/reports/definitions'),
                axios.get('/reports/my-reports'),
                axios.get('/reports/overview')
            ]);

            if (defsRes.status === 'fulfilled') {
                setDefinitions(defsRes.value.data.data || []);
            }
            if (reportsRes.status === 'fulfilled') {
                setMyReports(reportsRes.value.data.data || []);
            }
            if (overviewRes.status === 'fulfilled') {
                setOverview(overviewRes.value.data.data || null);
            } else {
                // Fallback: default dashboard (also live after Step 13)
                try {
                    const dash = await axios.get('/reports/dashboard/default');
                    setOverview(dash.data.data?.data || null);
                } catch (_) {
                    setOverview(null);
                }
            }

            if (defsRes.status === 'rejected' && reportsRes.status === 'rejected') {
                toast.error('Failed to load reports');
            }
        } catch (error) {
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const generateReport = async (definitionId, parameters = {}) => {
        setGenerating(definitionId);
        try {
            await axios.post(`/reports/generate/${definitionId}`, parameters);
            toast.success('Report generated successfully!');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to generate report');
        } finally {
            setGenerating(null);
        }
    };

    const downloadReport = async (report) => {
        setDownloading(report.id);
        try {
            const response = await axios.get(`/reports/instance/${report.id}/download`, {
                responseType: 'blob',
            });

            const blob = new Blob([response.data], {
                type: response.headers['content-type'] || 'application/octet-stream',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = report.file_name || `${(report.title || 'report').replace(/[^\w.-]+/g, '_')}.${report.format || 'pdf'}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            let message = 'Failed to download report';
            if (error.response?.data instanceof Blob) {
                try {
                    const json = JSON.parse(await error.response.data.text());
                    message = json.message || message;
                } catch (_) {
                    // keep default message
                }
            } else if (error.response?.data?.message) {
                message = error.response.data.message;
            }
            toast.error(message);
        } finally {
            setDownloading(null);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-56" />
                <div className="skeleton h-40" />
                <span className="sr-only">Loading reports…</span>
            </div>
        );
    }

    const categoryLabels = {
        intern: 'Intern Reports',
        hours: 'Hours Reports',
        learning: 'Learning Reports',
        business: 'Business Reports',
        mentor: 'Mentor Reports',
        program: 'Program Reports',
        talent: 'Talent Reports'
    };

    const groupedDefinitions = definitions.reduce((acc, def) => {
        if (!acc[def.category]) acc[def.category] = [];
        acc[def.category].push(def);
        return acc;
    }, {});

    const widgetValue = (meta) => {
        if (!overview) return null;
        const raw = overview[meta.key] ?? (meta.alt ? overview[meta.alt] : null);
        if (raw === null || raw === undefined) return null;
        return meta.suffix ? `${raw}${meta.suffix}` : String(raw);
    };

    return (
        <div className="w-full space-y-6">
            <PageHeader
                title="Reports & Analytics"
                description="Generate exports and view live program metrics (no student ranking scores)."
            />

            <div className="card !p-0 overflow-hidden animate-fade-up">
                <div className="border-b border-border flex space-x-4 px-4 bg-ink-50/50">
                    {[
                        { id: 'my-reports', label: 'My Generated Reports', icon: DocumentTextIcon },
                        { id: 'generate', label: 'Report Templates', icon: PlusIcon },
                        { id: 'analytics', label: 'Analytics Widgets', icon: ChartBarIcon },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                                activeTab === tab.id
                                    ? 'border-brand-700 text-brand-800'
                                    : 'border-transparent text-ink-500 hover:text-ink-800'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>

                <div className="p-6">
                    {activeTab === 'my-reports' && (
                        <div className="space-y-4">
                            <h3 className="text-base font-semibold text-ink-950">Generated Reports History</h3>
                            {myReports.length === 0 ? (
                                <p className="text-ink-500 text-sm py-4">No reports generated yet.</p>
                            ) : (
                                <div className="space-y-2">
                                    {myReports.map((report) => (
                                        <div key={report.id} className="flex items-center justify-between p-3 rounded-xl border border-border text-sm hover:bg-ink-50 transition-colors">
                                            <div>
                                                <p className="font-semibold text-ink-950">{report.title}</p>
                                                <div className="flex items-center space-x-3 text-xs text-ink-500 mt-0.5">
                                                    <span className="capitalize font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-200">{report.status}</span>
                                                    <span>{new Date(report.created_at || report.generated_at).toLocaleDateString()}</span>
                                                    <span>{report.format}</span>
                                                </div>
                                            </div>
                                            {report.file_url && (
                                                <button
                                                    type="button"
                                                    onClick={() => downloadReport(report)}
                                                    disabled={downloading === report.id}
                                                    className="px-3 py-1.5 bg-brand-700 text-white text-xs font-medium rounded hover:bg-brand-800 flex items-center space-x-1 disabled:opacity-50"
                                                >
                                                    {downloading === report.id ? (
                                                        <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                                                    )}
                                                    <span>{downloading === report.id ? 'Downloading...' : 'Download'}</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'generate' && (
                        <div className="space-y-6">
                            <h3 className="text-base font-semibold text-ink-950">Report Templates Library</h3>
                            {Object.keys(groupedDefinitions).length === 0 ? (
                                <p className="text-sm text-ink-500">No report templates available.</p>
                            ) : (
                                Object.entries(groupedDefinitions).map(([category, defs]) => (
                                    <div key={category} className="space-y-3">
                                        <h4 className="text-xs font-bold text-ink-500 uppercase tracking-wider">
                                            {categoryLabels[category] || category}
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {defs.map((def) => (
                                                <div key={def.id} className="rounded-xl border border-border p-4 flex flex-col justify-between space-y-2">
                                                    <div>
                                                        <h5 className="font-bold text-sm text-ink-950">{def.display_name}</h5>
                                                        <p className="text-xs text-ink-500 mt-1">{def.description}</p>
                                                    </div>
                                                    <button
                                                        onClick={() => generateReport(def.id)}
                                                        disabled={generating === def.id}
                                                        className="w-full py-1.5 bg-brand-700 text-white text-xs font-medium rounded hover:bg-brand-800 flex items-center justify-center space-x-1 disabled:opacity-50"
                                                    >
                                                        {generating === def.id ? (
                                                            <>
                                                                <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                                                                <span>Generating...</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PlusIcon className="w-3.5 h-3.5" />
                                                                <span>Generate Report</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === 'analytics' && (
                        <div className="space-y-4">
                            <h3 className="text-base font-semibold text-ink-950">Program Overview Dashboard</h3>
                            {!overview ? (
                                <p className="text-sm text-ink-500">
                                    Live analytics unavailable (missing permission or data).
                                </p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {WIDGET_META.map((meta) => {
                                        const value = widgetValue(meta);
                                        if (value === null) return null;
                                        return (
                                            <WidgetCard
                                                key={meta.key}
                                                title={meta.title}
                                                value={value}
                                                unit={meta.unit}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const WidgetCard = ({ title, value, unit }) => (
    <div className="card p-4 space-y-1">
        <span className="text-xs font-medium text-ink-400 uppercase">{title}</span>
        <p className="text-2xl font-bold text-ink-950">{value}</p>
        <span className="text-xs text-ink-500">{unit}</span>
    </div>
);

export default Reports;
